import type { User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

// Task #700: the controlled, client-side twin of the Team Action Items page's
// filter row — same search box, All Owners, All Dates and sort select. The
// team page drives its filters through the server (ActionItemFilters); the
// in-meeting Accountability section filters the props it already has, so it
// renders this controlled version instead.

export interface ActionItemSortOption {
  value: string
  label: string
}

interface Props {
  search: string
  onSearchChange: (value: string) => void
  ownerId: string
  onOwnerChange: (value: string) => void
  dueDateRange: string
  onDueDateRangeChange: (value: string) => void
  sort: string
  onSortChange: (value: string) => void
  owners: User[]
  sortOptions: ActionItemSortOption[]
  hasActiveFilters: boolean
  onClear: () => void
}

export default function ActionItemFilterBar({
  search, onSearchChange,
  ownerId, onOwnerChange,
  dueDateRange, onDueDateRangeChange,
  sort, onSortChange,
  owners, sortOptions, hasActiveFilters, onClear,
}: Props) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Search action items..."
          aria-label="Search action items"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md outline-none focus:border-charcoal"
        />
      </div>

      <select
        value={ownerId}
        onChange={e => onOwnerChange(e.target.value)}
        aria-label="Filter by owner"
        className="text-sm border border-gray-200 rounded-md px-3 py-1.5 outline-none focus:border-charcoal"
      >
        <option value="">All Owners</option>
        {sortPeople(owners).map(m => (
          <option key={m.id} value={m.id}>{m.full_name}</option>
        ))}
      </select>

      <select
        value={dueDateRange}
        onChange={e => onDueDateRangeChange(e.target.value)}
        aria-label="Filter by due date"
        className="text-sm border border-gray-200 rounded-md px-3 py-1.5 outline-none focus:border-charcoal"
      >
        <option value="">All Dates</option>
        <option value="overdue">Overdue</option>
        <option value="today">Due Today</option>
        <option value="this_week">This Week</option>
      </select>

      <select
        value={sort}
        onChange={e => onSortChange(e.target.value)}
        aria-label="Sort action items"
        className="text-sm border border-gray-200 rounded-md px-3 py-1.5 outline-none focus:border-charcoal"
      >
        {sortOptions.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>

      {hasActiveFilters && (
        <button
          onClick={onClear}
          className="text-sm text-text-secondary hover:text-text-primary underline"
        >
          Clear filters
        </button>
      )}
    </div>
  )
}
